import { HTMLAttributes, useEffect, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import useEmblaCarousel from 'embla-carousel-react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import Spacer from '@/components/sections/spacer'
import useStableViewport from '@/hooks/use-stable-viewport'

import Media from '../base/media'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPMedia } from '@/types/wp'

gsap.registerPlugin(ScrollTrigger)

type SustainabilityPushProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    position: 'right_image' | 'left_image'
    items: {
      pretitle: string
      title: string
      text: string
      image: WPImage
      media: WPMedia
      link: WPLink
    }[]
  }>

const SustainabilityPush = ({
  position = 'right_image',
  items,
  ...props
}: SustainabilityPushProps) => {
  const { isMobile } = useStableViewport()
  const [activeIndex, setActiveIndex] = useState(0)
  const desktopRef = useRef<HTMLDivElement>(null)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    active: (items?.length ?? 0) > 1
  })

  useEffect(() => {
    if (!emblaApi || !isMobile) return

    const onSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, isMobile])

  useGSAP(
    () => {
      if (!desktopRef.current || !items?.length || isMobile) return

      const selector = gsap.utils.selector(desktopRef)

      const motionItems = selector('[data-motion-item]')
      const motionImages = selector('[data-motion-image]')
      const motionImagesImg = selector('[data-motion-image] img')

      if (!motionItems.length || !motionImages.length) return

      const PAUSE = 1
      const TRANSITION = 1

      // Stato iniziale robusto, così non dipendi solo dalle classi Tailwind
      gsap.set(motionItems, { opacity: 0 })
      gsap.set(motionImages, { yPercent: 100 })

      gsap.set(motionItems[0], { opacity: 1 })
      gsap.set(motionImages[0], { yPercent: 0 })

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: desktopRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: true,
          invalidateOnRefresh: true,
          invalidateOnResize: true
        }
      })

      // Per ogni transizione i -> i+1
      for (let i = 0; i < items.length - 1; i++) {
        // pausa
        timeline.to({}, { duration: PAUSE })

        // transizione simultanea
        timeline.to(
          motionItems[i],
          {
            opacity: 0,
            duration: TRANSITION,
            ease: 'none'
          },
          '>'
        )

        timeline.to(
          motionImages[i],
          {
            yPercent: -50,
            scale: 1.1,
            duration: TRANSITION,
            ease: 'none'
          },
          '<'
        )
        timeline.to(
          motionImagesImg[i],
          {
            opacity: 0.5,
            duration: TRANSITION,
            ease: 'none'
          },
          '<'
        )

        timeline.to(
          motionItems[i + 1],
          {
            opacity: 1,
            duration: TRANSITION,
            ease: 'none'
          },
          '<'
        )

        timeline.to(
          motionImages[i + 1],
          {
            yPercent: 0,
            scale: 1,
            duration: TRANSITION,
            ease: 'none'
          },
          '<'
        )

        timeline.to(
          motionImagesImg[i + 1],
          {
            opacity: 1,
            duration: TRANSITION,
            ease: 'none'
          },
          '<'
        )
      }
    },
    {
      scope: desktopRef,
      dependencies: [items, isMobile]
    }
  )

  // Mobile: GSAP fadeIn
  const mobileMotionRef = useRef<HTMLDivElement>(null)

  useGSAP(
    () => {
      if (!isMobile || !mobileMotionRef.current) return

      gsap.fromTo(
        mobileMotionRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: mobileMotionRef.current,
            once: true,
            start: 'top 82%',
            invalidateOnRefresh: true,
            toggleActions: 'play none none none'
          }
        }
      )
    },
    {
      scope: mobileMotionRef,
      dependencies: [items, isMobile],
      revertOnUpdate: true
    }
  )

  const renderDesktop = () => (
    <div
      style={{
        height: `calc(100 * var(--lvh) * ${items?.length})`
      }}
      ref={desktopRef}
    >
      <Grid className="sticky top-0 h-[calc(100*var(--lvh))]">
        <div
          className={cn('relative h-full', 'md:col-span-3 lg:col-span-5', {
            'order-2 lg:col-start-8': position === 'left_image'
          })}
        >
          {items &&
            items?.map((item, index) => {
              return (
                <div
                  key={index}
                  className={cn(
                    'absolute left-0 top-0 flex h-full w-full shrink-0 items-center opacity-0',
                    {
                      'opacity-100': index === 0
                    }
                  )}
                  data-motion-item={index}
                >
                  <div className="flex flex-col gap-spacing-lg">
                    <Typography
                      variant="regular"
                      className="condensed text-d-regular text-text-primary-light dark:text-text-primary-dark"
                    >
                      {item.pretitle}
                    </Typography>
                    <Typography variant="h3" className="text-s-h3">
                      {item.title}
                    </Typography>
                    <Typography
                      variant="regular"
                      className="text-d-regular text-text-primary-light dark:text-text-primary-dark"
                    >
                      {item.text}
                    </Typography>
                    {item.link?.url ? (
                      <div className="flex-start flex flex-[1_0_auto] items-end">
                        <CtaButton href={item.link.url} variant="primary">
                          {item.link.title}
                        </CtaButton>
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            })}
        </div>
        <div
          className={cn(
            'relative flex h-[calc(100*var(--lvh))] items-center overflow-hidden',
            'md:col-span-3 lg:col-span-6',
            {}
          )}
        >
          <div className="relative aspect-[3/4] h-auto w-full overflow-hidden">
            {items &&
              items?.map((item, index) => {
                const hasMedia =
                  item.media?.poster?.url || item.media?.video?.url
                return (
                  <div
                    key={index}
                    data-motion-image={index}
                    className="absolute inset-0 bg-[black]"
                    style={{ zIndex: index }}
                  >
                    {hasMedia ? (
                      <Media
                        media={item.media}
                        className="aspect-[3/4] h-full w-full object-cover"
                      />
                    ) : (
                      <img
                        src={item.image.url}
                        alt={item.image.alt || ''}
                        className="aspect-[3/4] h-full w-full object-cover"
                        data-lightbox
                      />
                    )}
                  </div>
                )
              })}
          </div>
        </div>
      </Grid>
    </div>
  )

  const renderMobile = () => (
    <Grid>
      <div className="col-span-2">
        <div ref={mobileMotionRef} data-motion-gallery className="opacity-0">
          <div ref={emblaRef} className="relative select-none overflow-hidden">
            <div className="flex gap-spacing-sm">
              {items &&
                items?.map((item, index) => {
                  const hasMedia =
                    item.media?.poster?.url || item.media?.video?.url

                  return (
                    <div
                      key={index}
                      className="flex w-full shrink-0 flex-col gap-spacing-lg"
                    >
                      <div
                        key={index}
                        data-motion-image={index}
                        className={cn('w-full', {
                          'order-2': position === 'right_image'
                        })}
                      >
                        {hasMedia ? (
                          <Media
                            media={item.media}
                            className="aspect-[3/4] h-full w-full object-cover"
                          />
                        ) : (
                          <img
                            src={item.image.url}
                            alt={item.image.alt || ''}
                            className="aspect-[3/4] h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex h-full flex-col gap-spacing-lg">
                        <Typography
                          variant="regular"
                          className="condensed text-d-regular text-text-primary-light dark:text-text-primary-dark"
                        >
                          {item.pretitle}
                        </Typography>
                        <Typography variant="h3" className="text-s-h3">
                          {item.title}
                        </Typography>
                        <Typography
                          variant="regular"
                          className="text-d-regular text-text-primary-light dark:text-text-primary-dark"
                        >
                          {item.text}
                        </Typography>
                        {item.link?.url ? (
                          <div className="flex-start flex flex-[1_0_auto] items-end">
                            <CtaButton href={item.link.url} variant="primary">
                              {item.link.title}
                            </CtaButton>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
          {(items?.length ?? 0) > 1 && (
            <div className="flex items-center justify-between pt-spacing-lg">
              <Typography variant="large" className="condensed">
                {(activeIndex + 1).toString().padStart(2, '0')}
              </Typography>
              <div className="flex items-end justify-end gap-spacing-2xs self-end">
                <button
                  className="flex size-[32px] items-center justify-center rounded-radius-minimal border border-border-secondary-light"
                  onClick={() => emblaApi?.scrollPrev()}
                >
                  <SVGIcon symbol="arrow-prev" className="size-[12px]" />
                </button>
                <button
                  className="flex size-[32px] items-center justify-center rounded-radius-minimal border border-border-secondary-light"
                  onClick={() => emblaApi?.scrollNext()}
                >
                  <SVGIcon symbol="arrow-next" className="size-[12px]" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Grid>
  )

  return (
    <Section {...props} className="relative z-10">
      <Spacer variant="xxl" />
      <Container>{isMobile ? renderMobile() : renderDesktop()}</Container>
      <Spacer variant="xxl" />
    </Section>
  )
}

export default SustainabilityPush
